import React, { useState, useRef } from "react";
import TauriLoader2 from "../TauriLoader2/TauriLoader2";
import TauriImage from "../TauriImage/TauriImage";
import TauriCustomPaganation from "../TauriCustomPaganation/TauriCustomPaganation";
import { Dropdown } from "react-bootstrap";
import './OpalDataTable.css';
import { createPortal } from "react-dom";
import config from '../../config.json';
import { formatNumber } from "../../utils/NumberFormatter";

const OpalDataTable = ({
    loading = false,
    data = [],
    columns = [],
    actions = [],
    currentPage = 1,
    totalPages = 1,
    totalItems = 0,
    displayPerPage = 10,
    enablePaging = false,
    onPageChange,
    onDisplayPerPage,
    onRowClick,
    isDisabled = false
}) => {
    const [openDropdownRow, setOpenDropdownRow] = useState(null);
    const [editingValues, setEditingValues] = useState({});
    const tableRef = useRef(null);

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-4">
                <TauriLoader2
                    width="2.5rem"
                    height="2.5rem"
                    borderwidth="0.35rem"
                    color="#1D5D9B"
                />
            </div>
        );
    }

    if (!data.length) return null;

    const getValue = (row, key) =>
        key.split(".").reduce((o, k) => o?.[k], row);

    const getEditKey = (rowId, colKey) => `${rowId}_${colKey}`;

    const getColumnTotal = (colKey) => {
        return data.reduce((sum, row) => {
            const val = Number(getValue(row, colKey));
            return !isNaN(val) ? sum + val : sum;
        }, 0);
    };

    const hasTotalColumn = columns.some(col => col.total === true);

    return (
        <div className="opal-data-table-container" ref={tableRef}>
            <div className="opal-data-table-scroll">
                <table>
                    <thead>
                        <tr>
                            {actions.length > 0 && <th></th>} {/* First column for actions */}
                            {columns.map(col => <th key={col.key}>{col.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, idx) => {
                            const rowId = row.ID ?? row.Id ?? Object.values(row)[0];

                            return (
                                <tr
                                    key={rowId}
                                    className={idx % 2 === 0 ? "" : "even"}
                                    onClick={() => {
                                        setOpenDropdownRow(null);
                                        onRowClick?.(row);
                                    }}
                                    style={{ cursor: onRowClick ? "pointer" : "default" }}
                                >
                                    {actions.length > 0 && (
                                        <td onClick={e => e.stopPropagation()}>
                                            <Dropdown
                                                show={openDropdownRow === rowId}
                                                onToggle={isOpen => setOpenDropdownRow(isOpen ? rowId : null)}
                                            >
                                                <Dropdown.Toggle
                                                    as="button"
                                                    className="btn btn-light btn-sm"
                                                >
                                                    <TauriImage localImage="/Images/mdi--ellipsis-horizontal.svg" />
                                                </Dropdown.Toggle>

                                                {createPortal(
                                                    <Dropdown.Menu className="opal-data-table-container-dropdownmenu" style={{
                                                        position: "absolute",
                                                        zIndex: 9999
                                                    }}>
                                                        {actions.map(action => (
                                                            <Dropdown.Item
                                                                key={action.label}
                                                                onClick={() => action.onClick(row)}
                                                                className={action.danger ? "text-danger" : ""}
                                                            >
                                                                {action.label}
                                                            </Dropdown.Item>
                                                        ))}
                                                    </Dropdown.Menu>,
                                                    document.body
                                                )}


                                            </Dropdown>
                                        </td>
                                    )}

                                    {columns.map(col => {
                                        const editKey = getEditKey(rowId, col.key);
                                        const value =
                                            editingValues[editKey] ??
                                            getValue(row, col.key) ??
                                            "";

                                        const alignClass = col.inputType === "number" ? "text-end" : "";

                                        return (
                                            <td key={col.key} onClick={e => col.editable && e.stopPropagation()} className={alignClass}>
                                                {col.editable ? (
                                                    <input
                                                        type={col.inputType || "text"}
                                                        className={`opal-table-input ${col.inputType === "number" ? "text-end" : ""}`}
                                                        value={value}
                                                        readOnly={isDisabled}
                                                        onChange={e =>
                                                            setEditingValues(v => ({
                                                                ...v,
                                                                [editKey]: e.target.value
                                                            }))
                                                        }
                                                        onBlur={() => {
                                                            col.onBlur?.(row, value);
                                                            setEditingValues(v => {
                                                                const copy = { ...v };
                                                                delete copy[editKey];
                                                                return copy;
                                                            });
                                                        }}
                                                    />
                                                ) : col.render ? (
                                                    col.render(row)
                                                ) : (
                                                    col.inputType === "number"
                                                        ? formatNumber(value)  
                                                        : value
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>

                    {hasTotalColumn && (
                        <tr className="opal-total-row">
                            {actions.length > 0 && <td></td>}

                            {columns.map((col, index) => {
                                if (!col.total) {
                                    return (
                                        <td key={col.key}>
                                            {index === 0 ? <span>TOTAL:</span> : ""}
                                        </td>
                                    );
                                }

                                const total = getColumnTotal(col.key);

                                return (
                                    <td key={col.key} className="text-end"><span>{formatNumber(total)}</span></td>
                                );
                            })}
                        </tr>
                    )}

                </table>
            </div>

            {enablePaging && (
                <div className="opal-data-table-pagination">
                    <TauriCustomPaganation
                        currentPage={currentPage}
                        totalPages={totalPages}
                        displayPerPage={displayPerPage}
                        totalItems={totalItems}
                        onPageChange={onPageChange}
                        onDisplayPerPage={onDisplayPerPage}
                    />
                </div>
            )}
        </div>
    );
};

export default OpalDataTable;
